import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import DecimalRating from '../common/StarRating';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

function AddReviewSkeleton() {
    return (
        <div className="max-w-3xl mx-auto p-4">
            <div className="h-8 w-48 bg-gray-700 rounded mb-6"></div>
            <div className="w-full max-w-xl mx-auto p-4 bg-transparent rounded-lg shadow-none">
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 animate-pulse">
                    <div className="h-8 bg-gray-300 rounded mb-6 w-3/4"></div>
                    <div className="mb-4">
                        <div className="h-8 bg-gray-300 rounded w-full mb-2"></div>
                        <div className="h-10 bg-gray-300 rounded w-full"></div>
                    </div>
                    <div className="mb-4">
                        <div className="h-8 bg-gray-300 rounded w-full mb-2"></div>
                        <div className="h-24 bg-gray-300 rounded w-full"></div>
                    </div>
                    <div className="mb-6">
                        <div className="h-8 bg-gray-300 rounded w-full mb-2"></div>
                        <div className="h-10 bg-gray-300 rounded w-full"></div>
                    </div>
                    <div className="mb-4 flex items-center space-x-2">
                        <div className="h-4 w-4 bg-gray-300 rounded"></div>
                        <div className="h-6 w-24 bg-gray-300 rounded"></div>
                    </div>
                    <div className="mb-6">
                        <div className="h-12 bg-gray-300 rounded w-full"></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const AddReview = ({ prefilledName = '', variant = 'standalone', onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        name: prefilledName,
        review: '',
        rating: '',
        visibility: 'public',
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setFormData((prev) => ({ ...prev, name: prefilledName }));
    }, [prefilledName]);

    const handleChange = (e) => {
        const { name, type, checked, value } = e.target;
        if (type === 'checkbox' && name === 'visibility') {
            setFormData(prevState => ({
                ...prevState,
                visibility: checked ? 'public' : 'private',
            }));
        } else {
            setFormData(prevState => ({
                ...prevState,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);
        setLoading(true);

        try {
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                setError('You must be logged in to submit a review');
                setLoading(false);
                return;
            }

            const ratingNum = parseFloat(formData.rating);
            if (isNaN(ratingNum) || ratingNum < 1 || ratingNum > 10) {
                setError('Rating must be between 1 and 10');
                setLoading(false);
                return;
            }

            if (formData.review && formData.review.split(' ').filter(word => word.length > 0).length > 100) {
                setError('Review cannot exceed 100 words');
                setLoading(false);
                return;
            }

            const requestBody = {
                name: formData.name,
                review: formData.review || null,
                rating: ratingNum,
                user_id: user.id,
                visibility: formData.visibility,
            };

            console.log('Submitting review with data:', requestBody);

            const response = await fetch(`${API_BASE}/v1/reviews/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': API_KEY
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.log('API Error Response:', errorData);
                throw new Error(typeof errorData === 'object' ? JSON.stringify(errorData) : errorData.detail || 'Failed to submit review');
            }

            setFormData({
                name: prefilledName,
                review: '',
                rating: '',
                visibility: 'public'
            });
            setSuccess(true);
            setLoading(false);

            if (onSuccess) {
                setTimeout(onSuccess, 1500);
            }

            fetch(`${API_BASE}/v1/ai_recommendations/generate`, {
                method: 'POST',
                headers: {
                    'X-API-Key': API_KEY,
                    'Content-Type': 'application/json',
                    'X-User-ID': user.id
                },
            }).catch(err => console.warn('Error triggering recommendations:', err));

        } catch (err) {
            console.error('Error submitting review:', err);
            setError(err.message || 'Failed to submit review. Please try again.');
            setLoading(false);
        }
    };

    if (loading) {
        return <AddReviewSkeleton />;
    }

    if (variant === 'standalone') {
        return (
            <div className="max-w-3xl mx-auto p-4">
                <h2 className="text-xl font-bold mb-6 text-cling-pink">Add a review</h2>
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
                            Review submitted successfully! Generating fresh recommendations based on your review...
                        </div>
                    )}
                    <div className="mb-4 mt-0">
                        <label htmlFor="name" className="block text-gray-700 font-bold mb-2">
                            Film or TV series name
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 text-gray-900"
                        />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="review" className="block text-gray-700 font-bold mb-2">
                            Review
                        </label>
                        <textarea
                            id="review"
                            name="review"
                            value={formData.review}
                            onChange={handleChange}
                            rows="4"
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 text-gray-900"
                        />
                        {(() => {
                            const wordCount = formData.review.split(' ').filter(word => word.length > 0).length;
                            if (wordCount >= 80) {
                                return (
                                    <small className={`text-xs mt-1 block ${wordCount > 100 ? 'text-red-500' : 'text-yellow-600'}`}>
                                        {wordCount}/100 words
                                    </small>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    <div className="mb-6">
                        <DecimalRating
                            value={formData.rating || 0}
                            onChange={(newRating) => setFormData(prev => ({ ...prev, rating: newRating }))}
                            label="Rating"
                        />
                    </div>
                    <div className="mb-4 flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="visibility"
                            name="visibility"
                            checked={formData.visibility === 'public'}
                            onChange={handleChange}
                            className="w-4 h-4"
                        />
                        <label htmlFor="visibility" className="text-gray-700 font-semibold">
                            Make review public
                        </label>
                    </div>
                    <div className="mb-6">
                        <button
                            type="submit"
                            className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            Submit Review
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    const containerClasses = variant === 'standalone'
        ? "w-full max-w-3xl mx-auto p-4 bg-transparent"
        : "";
    const formClasses = variant === 'standalone'
        ? "bg-white rounded-2xl shadow-xl p-6 md:p-8"
        : "";

    return (
        <>
            <div className={containerClasses}>
                <form onSubmit={handleSubmit} className={formClasses}>
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
                            Review submitted successfully! Generating fresh recommendations based on your review...
                        </div>
                    )}
                    <div className="mb-4 mt-0">
                        <label htmlFor="name" className="block text-gray-700 font-bold mb-2">
                            Film or TV series name
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 text-gray-900"
                        />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="review" className="block text-gray-700 font-bold mb-2">
                            Review
                        </label>
                        <textarea
                            id="review"
                            name="review"
                            value={formData.review}
                            onChange={handleChange}
                            rows="4"
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 text-gray-900"
                        />
                        {(() => {
                            const wordCount = formData.review.split(' ').filter(word => word.length > 0).length;
                            if (wordCount >= 80) {
                                return (
                                    <small className={`text-xs mt-1 block ${wordCount > 100 ? 'text-red-500' : 'text-yellow-600'}`}>
                                        {wordCount}/100 words
                                    </small>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    <div className="mb-6">
                        <DecimalRating
                            value={formData.rating || 0}
                            onChange={(newRating) => setFormData(prev => ({ ...prev, rating: newRating }))}
                            label="Rating"
                        />
                    </div>
                    <div className="mb-4 flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="visibility"
                            name="visibility"
                            checked={formData.visibility === 'public'}
                            onChange={handleChange}
                            className="w-4 h-4"
                        />
                        <label htmlFor="visibility" className="text-gray-700 font-semibold">
                            Make review public
                        </label>
                    </div>
                    <div className="mb-6">
                        <button
                            type="submit"
                            className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            Submit Review
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default AddReview;

